import * as THREE from 'three';
import {
  FILO,
  INCLINAZIONE,
  LATO,
  RAD_TO_DEG,
  STROKE_FILO_MIN_PERP_MPS,
  STROKE_INCLINE_DIAG_DEG,
  STROKE_INCLINE_FENDENTE_DEG,
  STROKE_LATO_MIN_LATERAL_MPS,
  DOMINANT_HAND,
  type DominantHand,
  type Filo,
  type Inclinazione,
  type Lato,
} from '../config/constants';

export type { Lato, Inclinazione, Filo };

/**
 * A classified colpo along three orthogonal axes. An axis is `null` when undecidable
 * (too slow / ambiguous — mirrors `edgeAlignmentAngle` returning null). `lato` is also
 * `null` for fendente/montante, which are vertical and *sideless by definition*.
 */
export type StrokeClassification = {
  lato: Lato | null;
  inclinazione: Inclinazione | null;
  filo: Filo | null;
};

export type StrokeInput = {
  /**
   * Tip velocity at impact, world m/s. Its component ⊥ to the blade axis (the cutting
   * direction) drives all three axes — the same signal as the cut rose, free of the
   * windup contamination that pollutes the swing arc.
   */
  tipVelocityAtImpact: THREE.Vector3;
  /** Blade axis (unit, base→tip) at impact, world. */
  bladeAxisAtImpact: THREE.Vector3;
  /** Edge direction (filo dritto, +X local) at impact, world unit. */
  edgeDirAtImpact: THREE.Vector3;
  /** User's body "right" vector (world, horizontal, unit) — from the camera. */
  userRight: THREE.Vector3;
  /** Dominant (sword) hand — flips the lato sign convention. */
  dominantHand: DominantHand;
};

const ALL_AMBIGUOUS: StrokeClassification = { lato: null, inclinazione: null, filo: null };

/**
 * Classify a colpo from its impact pose. Pure: no side effects, no mutation.
 *
 * All three axes read the impact velocity's component perpendicular to the blade — the
 * cutting direction, the same vector the cut rose plots (which proved reliable on-device,
 * unlike the windup-contaminated swing arc). A pure vertical cut has no lateral component,
 * so its lato is honestly `null` (a single contact instant can't tell which shoulder it
 * came from). Each axis guards independently → one being ambiguous doesn't sink the others.
 */
export function classifyStroke(input: StrokeInput): StrokeClassification {
  const { tipVelocityAtImpact, bladeAxisAtImpact, edgeDirAtImpact, userRight, dominantHand } =
    input;

  if (
    !finite3(tipVelocityAtImpact) ||
    !finite3(bladeAxisAtImpact) ||
    !finite3(edgeDirAtImpact) ||
    !finite3(userRight)
  ) {
    return ALL_AMBIGUOUS;
  }

  // Cutting direction = impact velocity projected off the blade axis.
  const axisN = bladeAxisAtImpact.clone().normalize();
  const perp = tipVelocityAtImpact
    .clone()
    .sub(axisN.clone().multiplyScalar(tipVelocityAtImpact.dot(axisN)));

  // Inclinazione is the elevation of the cut in the user's FRONTAL plane: vertical speed vs
  // LATERAL (left–right) speed. The forward (toward-target) reach is dropped on purpose — it
  // varies with where on the pell you land (more reach-over to strike the crown than the flank),
  // so counting it made the same fendente read fendente at the flank but sgualembro at the top.
  // Blade-cant independent too: it reads the raw velocity, not the blade-relative `perp`.
  const inclinazione = classifyInclinazione(tipVelocityAtImpact, userRight);
  // Fendente and montante are vertical → sideless *by definition*: lato stays null (not a
  // failed measurement). Only diagonal/horizontal cuts (sgualembro/tondo/ridoppio) carry a lato.
  const lato = hasLato(inclinazione) ? classifyLato(perp, userRight, dominantHand) : null;

  return { inclinazione, lato, filo: classifyFilo(perp, edgeDirAtImpact) };
}

/** Whether the colpo's inclinazione carries a side (mandritto/riverso). Vertical cuts don't. */
function hasLato(inclinazione: Inclinazione | null): boolean {
  return (
    inclinazione === INCLINAZIONE.sgualembro ||
    inclinazione === INCLINAZIONE.tondo ||
    inclinazione === INCLINAZIONE.ridoppio
  );
}

/**
 * Elevation of the cut in the user's frontal plane (signed: − descending, + ascending) binned
 * into the 5 cuts. The horizontal reference is the LATERAL speed (|velocity · userRight|), not
 * the full ground-plane horizontal — this drops the forward reach toward the target, which would
 * otherwise vary with the impact zone and tilt a vertical fendente into sgualembro. Reads the raw
 * impact velocity, so it is also independent of how the blade is canted at contact. Null when the
 * in-plane (vertical + lateral) speed is too low to read a direction.
 */
function classifyInclinazione(
  velocity: THREE.Vector3,
  userRight: THREE.Vector3,
): Inclinazione | null {
  const right = userRight.clone();
  const rightLen = right.length();
  if (rightLen < 1e-6) return null;
  right.divideScalar(rightLen);

  const lateral = Math.abs(velocity.dot(right));
  if (Math.hypot(velocity.y, lateral) < STROKE_FILO_MIN_PERP_MPS) return null;
  // atan2(vertical, lateral): −90° straight down, 0° horizontal, +90° straight up.
  const elevationDeg = Math.atan2(velocity.y, lateral) * RAD_TO_DEG;

  if (elevationDeg <= -STROKE_INCLINE_FENDENTE_DEG) return INCLINAZIONE.fendente;
  if (elevationDeg <= -STROKE_INCLINE_DIAG_DEG) return INCLINAZIONE.sgualembro;
  if (elevationDeg < STROKE_INCLINE_DIAG_DEG) return INCLINAZIONE.tondo;
  if (elevationDeg < STROKE_INCLINE_FENDENTE_DEG) return INCLINAZIONE.ridoppio;
  return INCLINAZIONE.montante;
}

/**
 * Side from the lateral component of the cutting direction. For a right-hander a mandritto
 * sweeps right→left (lateral < 0); a riverso left→right. Left hand flips it. Null when the
 * cut is (near-)vertical (|lateral velocity| below the margin) — no side info at impact.
 */
function classifyLato(
  perp: THREE.Vector3,
  userRight: THREE.Vector3,
  dominantHand: DominantHand,
): Lato | null {
  const right = userRight.clone();
  const rightLen = right.length();
  if (rightLen < 1e-6) return null;
  right.divideScalar(rightLen);

  const lateral = perp.dot(right);
  if (Math.abs(lateral) < STROKE_LATO_MIN_LATERAL_MPS) return null;

  // Right hand: tip travelling left (lateral < 0) = mandritto. Left hand: mirror.
  const leadsFromDominantSide = dominantHand === DOMINANT_HAND.right ? lateral < 0 : lateral > 0;
  return leadsFromDominantSide ? LATO.mandritto : LATO.riverso;
}

/**
 * Which edge leads the cut: sign of (cutting direction · edgeDir). Positive → the true edge
 * (+X local) leads = filo dritto; negative → back edge = filo falso. Null when the
 * perpendicular speed is too low for either edge to clearly lead.
 */
function classifyFilo(perp: THREE.Vector3, edgeDir: THREE.Vector3): Filo | null {
  if (perp.length() < STROKE_FILO_MIN_PERP_MPS) return null;
  const s = perp.dot(edgeDir.clone().normalize());
  if (Math.abs(s) < 1e-6) return null;
  return s > 0 ? FILO.dritto : FILO.falso;
}

function finite3(v: THREE.Vector3): boolean {
  return isFinite(v.x) && isFinite(v.y) && isFinite(v.z);
}
